<template>
  <div class="space-y-3 pb-2">
    <div class="space-y-1.5 text-sm">
      <div class="text-xl font-semibold text-grey-light-900">Change Summary</div>

      <div class="flex items-center justify-between text-grey-light-700">
        <span>Total</span>
        <div class="flex items-center gap-2">
          <span class="text-grey-light-500 line-through">{{ displayCurrency(originalTotal) }}</span>
          <span class="font-semibold text-grey-light-900">{{ displayCurrency(amendedTotal) }}</span>
        </div>
      </div>

      <div class="flex items-center justify-between text-grey-light-700">
        <span>Total Paid</span>
        <span class="font-semibold text-grey-light-900">{{ displayCurrency(totalPaid) }}</span>
      </div>

      <div class="flex items-center justify-between text-grey-light-700">
        <span>Balance Due</span>
        <span class="font-semibold text-grey-light-900">{{ displayCurrency(balanceDue) }}</span>
      </div>
    </div>

    <div class="h-[1px] w-full bg-grey-light-200" />

    <div
      v-if="newTravelDate || newFinalPaymentDate"
      class="rounded-lg border border-brand-200 bg-brand-50 p-3 text-brand-700"
    >
      <div v-if="newTravelDate" class="flex items-center justify-between">
        <span class="font-semibold">New Travel Date</span>
        <span class="font-semibold">{{ displayDate(newTravelDate) }}</span>
      </div>
      <div v-if="newFinalPaymentDate" class="flex items-center justify-between" :class="newTravelDate ? 'pt-1' : ''">
        <span class="font-semibold">New Final Payment Date</span>
        <span class="font-semibold">{{ displayDate(newFinalPaymentDate) }}</span>
      </div>
    </div>

    <div class="h-[1px] w-full bg-grey-light-200" />

    <div class="space-y-2">
      <div class="text-xl font-semibold text-grey-light-900">Upcoming Installments</div>

      <div v-if="!visibleInstallments.length" class="text-sm text-grey-light-500">No upcoming installments.</div>

      <div
        v-for="payment in visibleInstallments"
        :key="payment.key"
        class="flex items-center justify-between text-grey-light-700"
      >
        <span>{{ payment.monthLabel }}</span>
        <span class="font-semibold text-grey-light-900">{{ displayCurrency(payment.amount) }}</span>
      </div>

      <button
        v-if="upcomingInstallments.length > defaultVisibleInstallments"
        type="button"
        class="flex w-full items-center justify-between text-left text-sm font-medium text-grey-light-700"
        @click="showAllInstallments = !showAllInstallments"
      >
        <span>{{ showAllInstallments ? 'View less' : 'View all' }}</span>
        <ChevronDown class="transition-all" :class="{ 'rotate-180': showAllInstallments }" />
      </button>

      <div class="flex items-center justify-between border-t border-grey-light-200 pt-2 text-grey-light-700">
        <span class="font-semibold">Total remaining</span>
        <span class="font-semibold text-grey-light-900">{{ displayCurrency(totalRemaining) }}</span>
      </div>
    </div>

    <div class="flex items-center gap-2 pt-1">
      <Button variant="outline" size="icon" class="shrink-0" @click="emit('back')">
        <ChevronLeft class="h-4 w-4" />
      </Button>
      <Button class="flex-1" @click="onViewSummary">Continue to summary</Button>
    </div>
    <div class="flex items-center gap-2 pt-1">
      <Button v-if="isAdmin" class="flex-1" @click="onSavePreview"> Save preview </Button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Button } from '@/components/ui/button';
import { useUserStore } from '@/stores/users';
import { displayCurrency, displayDate } from '@/utils/format.utils';
import dayjs from 'dayjs';
import { ChevronDown, ChevronLeft } from 'lucide-vue-next';
import { computed, ref } from 'vue';
import type { ModelOrderAmendmentPreviewResponse, ModelOrderResponse } from '@layup/api-sdk';

const props = defineProps<{
  preview: ModelOrderAmendmentPreviewResponse | null;
  order?: ModelOrderResponse;
}>();

const emit = defineEmits<{
  (e: 'back'): void;
  (e: 'save'): void;
  (e: 'view-summary', preview: ModelOrderAmendmentPreviewResponse | null): void;
}>();
const userStore = useUserStore();
const isAdmin = computed(() => userStore.user?.merchantRole === 'ADMIN');

const defaultVisibleInstallments = 2;
const showAllInstallments = ref(false);
const originalTotal = computed(() => {
  return props.preview?.from?.amountDue ?? props.order?.amountDue ?? 0;
});

const amendedTotal = computed(() => {
  const products = props.preview?.products ?? [];
  if (products.length) {
    return products.reduce((sum, product) => sum + (product.amount ?? 0), 0);
  }
  return props.preview?.amendedPlan?.reduce((sum, plan) => sum + (plan.amountDue ?? 0), 0) ?? originalTotal.value;
});

const totalPaid = computed(() => {
  const plans = props.preview?.originalPlan ?? props.order?.plans ?? [];
  return plans.reduce((sum, plan) => {
    const paidTotal = (plan.payments ?? []).reduce((paymentSum, payment) => {
      return paymentSum + (payment.paid ? (payment.amount ?? 0) : 0);
    }, 0);
    return sum + paidTotal;
  }, 0);
});

const balanceDue = computed(() => {
  const remaining = amendedTotal.value - totalPaid.value;
  return remaining > 0 ? remaining : 0;
});

const newTravelDate = computed(() => props.preview?.travelDate || props.preview?.suggestedTravelDate || '');
const newFinalPaymentDate = computed(() => props.preview?.endDate || '');

const upcomingInstallments = computed(() => {
  const plans = props.preview?.amendedPlan ?? [];
  const planWithPayments = plans.find((plan) => (plan.payments?.length ?? 0) > 0);
  const payments = planWithPayments?.payments ?? [];

  return payments
    .filter((payment) => !payment.paid)
    .sort((a, b) => dayjs(a.due).valueOf() - dayjs(b.due).valueOf())
    .map((payment, index) => ({
      key: `${payment._id || payment.due || index}`,
      monthLabel: payment.due ? dayjs(payment.due).format('MMMM') : `Installment ${index + 1}`,
      amount: payment.amount ?? 0,
    }));
});

const visibleInstallments = computed(() => {
  if (showAllInstallments.value) return upcomingInstallments.value;
  return upcomingInstallments.value.slice(0, defaultVisibleInstallments);
});

const totalRemaining = computed(() => {
  return upcomingInstallments.value.reduce((sum, payment) => sum + payment.amount, 0);
});

const onViewSummary = () => {
  emit('view-summary', props.preview);
};

const onSavePreview = () => emit('save');
</script>
